import { pb } from './pb'
import type { RecipeIngredient } from './recipes'

/**
 * Submitting a recipe to the shared pool.
 *
 * The payload mirrors `RecipesApi.submitRecipe` in the app field for field. The collection's
 * create rule requires `contributor` to be the signed-in user, `status` to be "pending" and
 * `verified` to be false, so those three are set here and are not the contributor's to choose:
 * `verified` is human approval and only a reviewer grants it.
 *
 * `clean_verified` is different and is set from the machine check that ran while she typed. It
 * records that the ingredients were checked against Leviticus 11 and nothing was questioned. A
 * reviewer still reads the recipe.
 */

/** Must match the `categories` Select on the server exactly. A value it does not know is
 *  rejected outright rather than stored, which is how "Invalid value bone_broth" once took a
 *  seed migration down. Same list as `RecipesApi.CATEGORY_OPTIONS`. */
export const CATEGORY_OPTIONS = [
  'breakfast',
  'lunch',
  'dinner',
  'everyday',
  'sabbath',
  'passover',
  'shavuot',
  'sukkot',
  'feast_of_trumpets',
  'yom_kippur',
  'hanukkah',
  'purim',
  'bone_broth',
  'light_soup',
  'lean_protein',
] as const

export const DIFFICULTY_OPTIONS = ['easy', 'medium', 'hard'] as const

export function categoryLabel(value: string): string {
  return value.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())
}

export interface RecipeDraft {
  title: string
  ingredients: RecipeIngredient[]
  instructions: string
  servings: number
  prepMinutes: number
  cookMinutes: number
  difficulty: string
  sabbathPrep: boolean
  categories: string[]
  caloriesPerServing: number | null
  proteinGrams: number | null
  carbsGrams: number | null
  fatGrams: number | null
  fiberGrams: number | null
  proteinTags: string[]
  cleanVerified: boolean
}

export async function submitRecipe(myUserId: string, draft: RecipeDraft): Promise<void> {
  const payload: Record<string, unknown> = {
    title: draft.title.trim(),
    contributor: myUserId,
    status: 'pending',
    verified: false,
    ingredients: draft.ingredients.map((item) => ({
      amount: item.amount,
      unit: item.unit,
      name: item.name,
    })),
    instructions: draft.instructions,
    servings: draft.servings,
    prep_minutes: draft.prepMinutes,
    cook_minutes: draft.cookMinutes,
    sabbath_prep: draft.sabbathPrep,
    categories: draft.categories,
    protein_tags: draft.proteinTags,
    clean_verified: draft.cleanVerified,
  }

  // Omitted rather than sent empty or zero. An unset number comes back as 0, and 0 calories is
  // a real value with a meaningless meaning, so writing one would put a zero-calorie meal into
  // somebody's plan.
  if (draft.difficulty) payload.difficulty = draft.difficulty
  if (draft.caloriesPerServing) payload.calories_per_serving = draft.caloriesPerServing
  if (draft.proteinGrams) payload.protein_grams = draft.proteinGrams
  if (draft.carbsGrams) payload.carbs_grams = draft.carbsGrams
  if (draft.fatGrams) payload.fat_grams = draft.fatGrams
  if (draft.fiberGrams) payload.fiber_grams = draft.fiberGrams

  await pb.collection('recipes').create(payload)
}

/**
 * What to say about a check result. Names the ingredient and cites the text, never scolds.
 *
 * The wording follows `RecipeCleanCheck.summary` except for UNVERIFIABLE, which in the app says
 * Torahly is not installed. That is not the reason here, and telling a woman on a laptop to
 * install an Android app would be nonsense.
 */
export function checkSummary(verdict: string): string {
  switch (verdict) {
    case 'CLEAN':
      return 'Every ingredient checks out against Leviticus 11.'
    case 'NEEDS_SOURCE':
      return "One or two ingredients don't say where they came from. Name the source and this can go through."
    case 'REJECTED':
      return "This one can't be shared, because of what the text says about these ingredients."
    default:
      return 'The ingredient list could not be reached, so this has not been checked yet. Your recipe is saved and will be checked before it is shared.'
  }
}
